import { api } from '@/lib/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type StatusAssinatura = 'PENDENTE' | 'ASSINADO' | 'INVALIDADO'

export interface StatusAssinaturaResponse {
  status: StatusAssinatura
  dataAssinatura?: string
  ultimaVersao?: number
}

export interface DocumentoAssinadoItem {
  id: number
  tipo_protocolo: string
  id_protocolo: number
  versao: number
  codigo_validacao: string
  data_assinatura: string
  assinante?: string
}

export function useStatusAssinaturaProtocoloQuery(
  prontuarioId: number | undefined,
  protocoloId: number | undefined,
  tipoProtocolo: string | undefined
) {
  return useQuery<StatusAssinaturaResponse, Error>({
    queryKey: ['status-assinatura', prontuarioId, protocoloId, tipoProtocolo],
    queryFn: async () => {
      if (!prontuarioId || !protocoloId || !tipoProtocolo) {
        return { status: 'PENDENTE' as StatusAssinatura }
      }
      const params = new URLSearchParams({ tipo_protocolo: tipoProtocolo })
      const res = (await api.get(
        `/api/prontuarios/${prontuarioId}/protocolos/${protocoloId}/status-assinatura?${params}`
      )) as { result: StatusAssinaturaResponse }
      return res.result
    },
    enabled: !!prontuarioId && !!protocoloId && !!tipoProtocolo
  })
}

export function useAssinarDocumentoMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      prontuarioId,
      protocoloId,
      tipoProtocolo
    }: {
      prontuarioId: number
      protocoloId: number
      tipoProtocolo: string
    }) => {
      const res = await api.post(
        `/api/prontuarios/${prontuarioId}/protocolos/${protocoloId}/assinar`,
        { tipo_protocolo: tipoProtocolo }
      )
      return res
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['status-assinatura', variables.prontuarioId, variables.protocoloId, variables.tipoProtocolo]
      })
      queryClient.invalidateQueries({
        queryKey: ['documentos-assinados', variables.prontuarioId]
      })
    }
  })
}

export function useHistoricoDocumentosAssinadosQuery(prontuarioId: number | undefined) {
  return useQuery<DocumentoAssinadoItem[], Error>({
    queryKey: ['documentos-assinados', prontuarioId],
    queryFn: async () => {
      if (!prontuarioId) return []
      const res = (await api.get(
        `/api/prontuarios/${prontuarioId}/documentos-assinados`
      )) as { result: DocumentoAssinadoItem[] }
      return res.result ?? []
    },
    enabled: !!prontuarioId
  })
}

export interface ResultadoValidacaoUpload {
  status: 'VÁLIDO' | 'INVÁLIDO' | 'NÃO_ENCONTRADO'
  empresa?: { nome?: string; cnpj?: string }
  medico?: string
  dataAssinatura?: string
  tipoDocumento?: string
}

export function useValidarUploadMutation() {
  return useMutation({
    mutationFn: async (file: File): Promise<ResultadoValidacaoUpload> => {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL
      if (!BASE_URL) throw new Error('URL da API não configurada.')
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`${BASE_URL}/api/assinaturas/validar-upload`, {
        method: 'POST',
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        credentials: 'include',
        body: formData
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data?.message ?? data?.error ?? 'Erro ao validar.')
      return data.result ?? data
    }
  })
}
